import React from 'react';

import { useSettings } from '@wix/tpa-settings/react';
import { useSettingsEvents } from '../../../../contexts/SettingsEvents/SettingsEvents';
import { useEnvironment, useBi } from '@wix/yoshi-flow-editor';
import { memberWebAppButtonClick as memberWebAppButtonClickV2 } from '@wix/bi-logger-challenges-member-web/v2';

import settingsParams from '../../settingsParams';
import { ButtonNames } from '../../../../contexts/main/biInterfaces';
import {
  ChallengeSettingsParams,
  ILayoutType,
  ProgramCardsAlignment,
} from '../../Settings/challengeSettings/challengeSettings.types';

import {
  Challenge,
  MemberChallenge,
} from '@wix/ambassador-challenges-v1-challenge/types';

import { ChallengeCard } from '../ChallengeCard';

import { classes, st } from './ChallengesList.st.css';
import { useIsFullWithInViewer } from '../../../../services/hooks/useIsFullWidthInViewer';
import { isProgramWidget } from '../../../../selectors/isProgramWidget';
import { FCWithChildren } from '@wix/challenges-web-library';
import { useCSSPBEnabled } from '../../../../hooks/useCSSPBStyle';
import { useChallengesList } from '../../../../contexts/storage-contexts/ChallengesList';
import { useMemberPaidPlans } from '../../../../contexts/storage-contexts/PaidPlans';

const getGridStyles = (
  isMobile: boolean,
  cssPBEnabled: boolean,
  settings: any,
) => {
  const layoutType = settings.get(settingsParams.layoutType);
  let gridTemplateColumns: string;

  if (isMobile) {
    gridTemplateColumns = `repeat(auto-fit, minmax(max(200px, 50%), 1fr))`;
  } else {
    const maxPercents = settings.get(settingsParams.itemsPerRow);
    const maxPercentsOutput = 100 / (maxPercents + 1);

    gridTemplateColumns = `repeat(auto-fit, minmax(max(240px, ${maxPercentsOutput.toFixed(
      2,
    )}%), 1fr))`;
  }

  return layoutType === ILayoutType.Grid
    ? {
        gridTemplateColumns: cssPBEnabled
          ? `repeat(auto-fit, minmax(max(240px, calc(100% / (var(--opgItemsPerRow) + 1))), 1fr))`
          : gridTemplateColumns,
        gap: cssPBEnabled
          ? 'calc(var(--opgCardSpacing) * 1px)'
          : `${settings.get(settingsParams.cardSpacing)}px`,
        justifyItems:
          settings.get(settingsParams.itemsPerRow) === 1 || isMobile
            ? `center`
            : null,
      }
    : {
        gridTemplateColumns: `1fr`,
        gap: cssPBEnabled
          ? 'calc(var(--opgSideCardsVerticalSpacing) * 1px)'
          : `${settings.get(settingsParams.sideCardsVerticalSpacing)}px`,
        justifyItems: `center`,
      };
};

const DIRECTION = {
  [ProgramCardsAlignment.Left]: 'ltr',
  [ProgramCardsAlignment.Right]: 'rtl',
};

export const ChallengesList: FCWithChildren<{
  challenges: MemberChallenge[];
}> = ({ challenges }) => {
  const settings = useSettings();
  const isFullWidth = useIsFullWithInViewer();
  const layoutType = settings.get(settingsParams.layoutType);
  const { isMobile, widgetId } = useEnvironment();
  const { userPaidPlans } = useMemberPaidPlans();
  const programListData = useChallengesList();
  const { buttonState } = useSettingsEvents();
  const bi = useBi();
  const cssPBEnabled = useCSSPBEnabled();

  const beforeGoToProgramPage = React.useCallback((challenge: Challenge) => {
    if (bi.report) {
      bi.report(
        memberWebAppButtonClickV2({
          buttonName: ButtonNames.ViewChallenge,
          challengeId: challenge?.id,
        }),
      );
    }
  }, []);

  return (
    <ul
      dir={
        layoutType === ILayoutType.Grid
          ? DIRECTION[
              settings.get(
                settingsParams[ChallengeSettingsParams.ProgramCardsAlignment],
              )
            ]
          : null
      }
      data-hook="challenge-list"
      className={st(classes.root, {
        mobile: isMobile,
        isProgramPage: !isProgramWidget(widgetId),
      })}
      style={getGridStyles(isMobile, cssPBEnabled, settings)}
    >
      {challenges.map((ch, ind) => {
        const challenge: Challenge = ch?.challenge;

        return (
          <li className={classes.item} key={ind}>
            {challenge ? (
              <ChallengeCard
                memberChallenge={ch}
                isFullWidth={isFullWidth}
                goToPage={() => {
                  beforeGoToProgramPage(challenge);
                  programListData.goToProgramPage(challenge.id);
                }}
                userPaidPlans={userPaidPlans}
                buttonState={buttonState}
                cardIndex={ind}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};
